import {
  AppCard,
  Button,
  FormAsyncAutocomplete,
  FormStaticAutocomplete,
  FormTextField,
  RouteLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Box, Container, Divider, Typography } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import axios from "axios";
import { useSnackbar } from "notistack";
import { type FC, useEffect, useRef, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { EquinorLogo } from "../../../../components/atoms/EquinorLogo/EquinorLogo";
import {
  Recaptcha,
  type RecaptchaRef,
} from "../../../../components/atoms/Recaptcha/Recaptcha";
import { config } from "../../../../config";
import {
  type SignUpFormValues,
  signUpFormSchema,
  signUpSearchSchema,
} from "./-schemas/signUpSchema";

interface TimezoneOption {
  display_name: string;
  formatted_offset: string;
  identifier: string;
  name: string;
}

interface TimezoneResponse {
  current_page: number;
  items_per_page: number;
  results: TimezoneOption[];
  total_items: number;
  total_pages: number;
}

async function fetchInvitationByToken(
  token: string,
): Promise<{ email: string; status: string }> {
  const url = `/vsupplierregistration/api/v2/vendor_invitations/${encodeURIComponent(token)}`;
  const res = await axios.get(url, { withCredentials: true });
  return res.data;
}

// 2. O Componente React
const SignUp: FC = () => {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [defaultEmail, setDefaultEmail] = useState("");
  const recaptchaRef = useRef<RecaptchaRef>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const languageOptions = ["en", "pt-BR"];
  const accessToken = search.access_token || search.accessToken || "";

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      accessToken,
      defaultEmail: "",
      email: "",
      first_name: "",
      last_name: "",
      locale: "en",
      password: "",
      password_confirmation: "",
      timezone: null,
    },
    mode: "onChange",
    resolver: zodResolver(signUpFormSchema),
  });

  const onSubmit: SubmitHandler<SignUpFormValues> = async (values) => {
    const token = recaptchaRef.current?.getValue();
    if (
      !token &&
      !(window.environment === "test" || window.environment === "development")
    ) {
      enqueueSnackbar(t("auth.signIn.invalidBotVerificationMessage"));
      return;
    }
    try {
      setLoading(true);
      setErrorMessage(null);

      const bodyFormData = new FormData();

      const email = values.email || values.defaultEmail || "";

      bodyFormData.append("user[first_name]", values.first_name);
      bodyFormData.append("user[last_name]", values.last_name);
      bodyFormData.append("user[locale]", values.locale);
      bodyFormData.append("user[timezone]", values.timezone?.identifier ?? "");
      bodyFormData.append("user[email]", email);
      bodyFormData.append("user[password]", values.password);
      bodyFormData.append(
        "user[password_confirmation]",
        values.password_confirmation,
      );

      if (values.defaultEmail) {
        bodyFormData.append("default_email", values.defaultEmail);
      }
      if (values.accessToken) {
        bodyFormData.append("access_token", values.accessToken);
      }

      bodyFormData.append("g-recaptcha-response", token ?? "");

      const response = await axios.postForm("/users", bodyFormData, {
        maxRedirects: 0,
        validateStatus: () => true,
        xsrfCookieName: config.csrfTokenCookieName,
        xsrfHeaderName: "x-csrf-token",
      });

      if (response.status >= 200 && response.status < 400) {
        enqueueSnackbar(t("auth.resendConfirmation.successMessage"), {
          variant: "success",
        });
        await navigate({ to: "/users/sign_in" });
        return;
      }

      const msg =
        response?.data?.errors?.join?.("\n") ||
        response?.data?.error ||
        response?.data?.message ||
        t("common.genericError");
      setErrorMessage(typeof msg === "string" ? msg : JSON.stringify(msg));
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      setErrorMessage(errorMsg || t("common.genericError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (accessToken) {
      (async () => {
        try {
          const invite = await fetchInvitationByToken(accessToken);
          if (!mounted) return;
          if (invite?.email) {
            setValue("email", invite.email);
            setValue("defaultEmail", invite.email);
            setDefaultEmail(invite.email);
          }
          setValue("accessToken", accessToken);
        } catch {
          await navigate({ href: "/403" });
        }
      })();
    }
    return () => {
      mounted = false;
    };
  }, [accessToken, navigate, setValue]);

  return (
    <Container
      maxWidth="md"
      sx={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "16px !important",
      }}
    >
      <AppCard slotProps={{ root: { sx: { padding: 3 } } }}>
        <EquinorLogo />
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography component="h1" sx={{ textAlign: "center" }} variant="h5">
            {t("common.signUp")}
          </Typography>
        </Box>
        <form name="signUpForm" noValidate onSubmit={handleSubmit(onSubmit)}>
          <input
            name="default_email"
            type="hidden"
            value={getValues("defaultEmail")}
          />
          <input name="access_token" type="hidden" value={accessToken} />

          {!!errorMessage && (
            <Alert
              onClose={() => setErrorMessage(null)}
              severity="error"
              sx={{ marginBottom: 3 }}
            >
              {errorMessage}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "row", gap: 3 }}>
              <FormTextField
                control={control}
                fullWidth
                helperText={errors.first_name?.message}
                label={t("common.firstName")}
                name="first_name"
                required
                size="small"
              />

              <FormTextField
                control={control}
                fullWidth
                helperText={errors.last_name?.message}
                label={t("common.lastName")}
                name="last_name"
                required
                size="small"
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "row", gap: 3 }}>
              <FormStaticAutocomplete
                control={control}
                getOptionLabel={(option) =>
                  option === "en"
                    ? t("common.english")
                    : t("common.portugueseBrazil")
                }
                isOptionEqualToValue={(option, value) => option === value}
                label={t("common.language")}
                name="locale"
                options={languageOptions}
              />

              <FormAsyncAutocomplete
                control={control}
                fetchOptions={async (params: {
                  items_per_page?: number;
                  page?: number;
                  search?: string;
                }) => {
                  const response = await axios.get<TimezoneResponse>(
                    "/api/v2/timezones",
                    {
                      params: {
                        display_name: params.search,
                        items_per_page: params.items_per_page,
                        page: params.page,
                      },
                      withCredentials: true,
                    },
                  );

                  return response.data;
                }}
                filters={(searchTerm: string) => ({ search: searchTerm })}
                getOptionLabel={(option: TimezoneOption) => option.display_name}
                helperText={errors.timezone?.message}
                isOptionEqualToValue={(
                  option: TimezoneOption,
                  value: string | TimezoneOption,
                ) =>
                  option?.identifier ===
                  (typeof value === "string" ? value : value?.identifier)
                }
                label={t("common.timezone")}
                name="timezone"
                placeholder={t("common.selectOption")}
                queryKey={["timezones"]}
              />
            </Box>

            <FormTextField
              control={control}
              disabled={!!defaultEmail}
              fullWidth
              helperText={errors.email?.message}
              label={t("common.email")}
              name="email"
              placeholder={defaultEmail || undefined}
              required
              size="small"
              type="email"
            />

            <FormTextField
              control={control}
              fullWidth
              helperText={
                errors.password?.message ||
                t("auth.signUp.passwordRequirementsMessage")
              }
              label={t("common.newPassword")}
              name="password"
              required
              size="small"
              type="password"
            />

            <FormTextField
              control={control}
              fullWidth
              helperText={errors.password_confirmation?.message}
              label={t("common.confirmNewPassword")}
              name="password_confirmation"
              required
              size="small"
              type="password"
            />

            <Recaptcha ref={recaptchaRef} />

            <Button
              fullWidth
              loading={loading}
              loadingPosition="center"
              sx={{ mb: 1, mt: 1 }}
              type="submit"
              variant="contained"
            >
              {t("common.signUp")}
            </Button>

            <Divider sx={{ margin: "8px 0px" }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                justifyContent: "space-between",
              }}
            >
              <RouteLink to="/users/sign_in">{t("common.signIn")}</RouteLink>
              <RouteLink to="/users/confirmation/new">
                {t("auth.signUp.resendConfirmationInstructions")}
              </RouteLink>
            </Box>
          </Box>
        </form>
      </AppCard>
    </Container>
  );
};

export const Route = createFileRoute("/_public/_auth/users/sign_up")({
  component: SignUp,
  validateSearch: (search) => signUpSearchSchema.parse(search),
});
